import { MessagePayload } from 'firebase/messaging';
import { useCallback } from 'react';
import { useCustomerPhonePermissions } from '../hooks/use-permissions';
import { getCurrentUserId } from '../../infra/firebase/init';
import { maskPhone } from '../../modules/home/v1/utils/mask-phone';

const usePushNotification = () => {
  const currentUserId = getCurrentUserId();
  const { getPhonePermissions } = useCustomerPhonePermissions();
  const hasAccessToReadPhone = getPhonePermissions(currentUserId ?? '').canRead;

  const maskNotificationTitle = useCallback(
    (payload: MessagePayload): MessagePayload => {
      const data = payload.data;
      if (!data || hasAccessToReadPhone) return payload;

      try {
        const chat = data.chatString ? JSON.parse(data.chatString) : {};
        const phoneNumber: string = chat.phoneNumber ?? '';
        const title: string = data.title ?? '';

        if (!phoneNumber || title !== phoneNumber) return payload;

        return {
          ...payload,
          data: { ...data, title: maskPhone(phoneNumber, 'X') },
        };
      } catch {
        return payload;
      }
    },
    [hasAccessToReadPhone]
  );

  const postNotificationToServiceWorker = useCallback((serviceWorker: ServiceWorker, payload: MessagePayload) => {
    serviceWorker.postMessage({
      type: 'SHOW_NOTIFICATION',
      payload,
    });
  }, []);

  const showPushNotification = useCallback(
    async (payload: MessagePayload) => {
      try {
        const maskedPayload = maskNotificationTitle(payload);
        if (!('serviceWorker' in navigator)) {
          return;
        }

        const controller = navigator.serviceWorker.controller;

        if (controller?.scriptURL.includes('firebase-messaging-sw.js')) {
          postNotificationToServiceWorker(controller, maskedPayload);
          return;
        }

        const registration =
          (await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js')) ??
          (await navigator.serviceWorker.register('/firebase-messaging-sw.js'));
        const activeServiceWorker = registration.active ?? (await navigator.serviceWorker.ready).active;

        if (activeServiceWorker) {
          postNotificationToServiceWorker(activeServiceWorker, maskedPayload);
        }
      } catch (error) {
        // Do nothing
      }
    },
    [postNotificationToServiceWorker, maskNotificationTitle]
  );

  return { showPushNotification };
};

export default usePushNotification;
